import React, { useMemo, useCallback } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "../Application/globaltypes/redux";
import { RolePermissions } from "../../enums";
import { AccountTypeEnum } from "../../interfaces/Account";
import RestrictedResource from "../Application/Restricted";
import MultiStateDropdown, {
  type MultiStateDropdownOption,
} from "../../components/dropdowns/multiStateDropdown/MultiStateDropdown";
import "./DomainManagement.scss";
import DomainsOverview from "./DomainsOverview";

enum PageViews {
  Domains = "Domains",
  DomainPools = "Domain Pools",
}

const viewOptions: MultiStateDropdownOption<PageViews>[] = [
  { label: PageViews.Domains },
  { label: PageViews.DomainPools },
];

const getDefaultOption = (view: PageViews) => viewOptions.find((o) => o.label === view) ?? viewOptions[0];

const DomainManagement = ({ accountTypeId }: PropsFromRedux) => {
  const [activeView, setActiveView] = React.useState(PageViews.Domains);

  const renderContentSwitcher = useCallback(
    () => (
      <MultiStateDropdown
        options={viewOptions}
        defaultOption={getDefaultOption(activeView)}
        onContextChange={(context: PageViews) => setActiveView(context)}
      />
    ),
    [activeView],
  );

  const components = useMemo<Partial<Record<PageViews, React.ReactElement>>>(
    () => ({
      [PageViews.Domains]: <DomainsOverview renderContentSwitcher={renderContentSwitcher} />,
      [PageViews.DomainPools]: <DomainsOverview renderContentSwitcher={renderContentSwitcher} />,
    }),
    [renderContentSwitcher],
  );

  if (accountTypeId !== AccountTypeEnum.BSI) {
    return null;
  }

  return (
    <RestrictedResource permissions={[RolePermissions.CampaignsManage]}>{components[activeView]}</RestrictedResource>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountTypeId: state.userProfile.accountTypeId,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedDomainManagement = connector(DomainManagement);
export default ConnectedDomainManagement;
